// =============================================================================
// Shared i18n core — SC-008 (Independent Gap Audit M00–M10)
//
// Platform-agnostic translation runtime shared by admin (Vite/React),
// vendor (Vite/React) and mobile (Expo/React Native). Per CFL-001 the locale
// catalogs and runtime live ONCE here under frontend/shared/i18n and are
// consumed by every workspace — no duplication.
//
// Design constraints that keep this file usable from BOTH React-DOM and
// React-Native bundles:
//   • NO direct DOM access (no `window`, `document`, `localStorage`,
//     `navigator`) and NO JSX (uses `createElement`) so Metro/Babel and Vite
//     both transform it cleanly.
//   • Persistence + device/browser locale detection are the HOST app's job:
//     the provider only accepts an `initialLocale` and emits `onLocaleChange`.
//     Web supplies a localStorage/navigator adapter (./web), mobile supplies
//     an AsyncStorage/NativeModules adapter (mobile/src/i18n).
//
// Catalogs are flat `key → string` maps (frontend/shared/i18n/<locale>.json)
// with `{var}` interpolation placeholders. Default locale is en-IN per
// docs/M00/Currency_Residency_Rule.md.
// =============================================================================
import { createContext, createElement, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import enIN from './en-IN.json';
import enUS from './en-US.json';
import hiIN from './hi-IN.json';

export type Locale = 'en-IN' | 'en-US' | 'hi-IN';

/** All locales that ship a catalog, in display order. */
export const LOCALES: readonly Locale[] = ['en-IN', 'en-US', 'hi-IN'];

/** Residency default — India English. See docs/M00/Currency_Residency_Rule.md. */
export const DEFAULT_LOCALE: Locale = 'en-IN';

export type Catalog = Readonly<Record<string, string>>;

export const catalogs: Readonly<Record<Locale, Catalog>> = {
  'en-IN': enIN as Catalog,
  'en-US': enUS as Catalog,
  'hi-IN': hiIN as Catalog,
};

const LOCALE_LABELS: Readonly<Record<Locale, string>> = {
  'en-IN': 'English (India)',
  'en-US': 'English (US)',
  'hi-IN': 'हिन्दी',
};

/** Human-readable name for a locale, in its own script where applicable. */
export function localeLabel(locale: Locale): string {
  return LOCALE_LABELS[locale] ?? locale;
}

/** Type-guard: is `value` one of the supported locale tags. */
export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/**
 * Normalise an arbitrary BCP-47-ish tag (e.g. 'hi', 'hi-IN', 'en_US',
 * 'en-GB') to a supported `Locale`, falling back to DEFAULT_LOCALE.
 * Used by host adapters to map a browser/device locale onto our catalog.
 */
export function resolveLocale(input: string | null | undefined): Locale {
  if (!input) return DEFAULT_LOCALE;
  if (isLocale(input)) return input;
  const lower = input.toLowerCase().replace('_', '-');
  if (lower.startsWith('hi')) return 'hi-IN';
  if (lower === 'en-us') return 'en-US';
  if (lower.startsWith('en')) return 'en-IN';
  return DEFAULT_LOCALE;
}

export type TranslateVars = Readonly<Record<string, string | number>>;

/**
 * Resolve `key` in `locale`'s catalog with a two-step fallback:
 * current locale → DEFAULT_LOCALE → the key itself (so a missing string is
 * visible in the UI rather than rendering blank). `{var}` placeholders are
 * substituted from `vars`; an absent var is left as `{var}` for visibility.
 */
export function translate(locale: Locale, key: string, vars?: TranslateVars): string {
  const primary = catalogs[locale] ?? catalogs[DEFAULT_LOCALE];
  const fallback = catalogs[DEFAULT_LOCALE];
  let template = primary[key] ?? fallback[key] ?? key;
  if (vars) {
    template = template.replace(/\{(\w+)\}/g, (_match, name: string) =>
      Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : `{${name}}`,
    );
  }
  return template;
}

export interface I18nContextValue {
  readonly locale: Locale;
  readonly setLocale: (locale: Locale) => void;
  readonly t: (key: string, vars?: TranslateVars) => string;
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

export interface I18nProviderProps {
  readonly children: ReactNode;
  /** Starting locale (host-detected). Defaults to DEFAULT_LOCALE. */
  readonly initialLocale?: Locale;
  /** Emitted whenever the locale changes so the host can persist it. */
  readonly onLocaleChange?: (locale: Locale) => void;
}

/**
 * Provides the active locale + bound `t()` to the tree. State lives here;
 * detection/persistence are injected by the host via `initialLocale` +
 * `onLocaleChange`, keeping this provider platform-agnostic.
 */
export function I18nProvider({
  children,
  initialLocale,
  onLocaleChange,
}: I18nProviderProps): ReactNode {
  const [locale, setLocaleState] = useState<Locale>(initialLocale ?? DEFAULT_LOCALE);

  const setLocale = useCallback(
    (next: Locale) => {
      setLocaleState((prev) => {
        if (prev !== next) onLocaleChange?.(next);
        return next;
      });
    },
    [onLocaleChange],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ locale, setLocale, t: (key, vars) => translate(locale, key, vars) }),
    [locale, setLocale],
  );

  return createElement(I18nContext.Provider, { value }, children);
}

/** Access the active locale, `setLocale`, and `t()`. Must be inside a provider. */
export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useI18n/useTranslation must be used within an <I18nProvider>');
  }
  return ctx;
}

/** Convenience alias mirroring the common `useTranslation()` ergonomics. */
export function useTranslation(): I18nContextValue {
  return useI18n();
}

/** Just the locale + setter, for language switchers. */
export function useLocale(): { readonly locale: Locale; readonly setLocale: (locale: Locale) => void } {
  const { locale, setLocale } = useI18n();
  return { locale, setLocale };
}

/**
 * The active locale, or the residency default when no provider is mounted.
 *
 * `useI18n` throws without a provider, and rightly so: a caller asking for
 * `t()` has no sensible answer without one, and a screen silently showing
 * translation keys is worse than a loud failure.
 *
 * Formatting is the other case. A component that only needs to know how the
 * reader writes a month must not be able to *crash* the screen it sits in —
 * and this is not hypothetical: `useBusinessDay` was given to the shared
 * ApprovalLevels tracker, and every test that renders it on its own began
 * throwing. 482 of the 527 admin test files mount no provider, which is a
 * fair measure of how many places a formatter could bring down.
 *
 * The fallback is `DEFAULT_LOCALE`, which is precisely what `I18nProvider`
 * itself falls back to when no `initialLocale` is supplied, so a tree with a
 * provider and a tree without one format identically.
 */
export function useLocaleOrDefault(): Locale {
  return useContext(I18nContext)?.locale ?? DEFAULT_LOCALE;
}
